<template>
	<div class="card card-custom card-stretch gutter-b">
		<div class="card-header border-0 pt-5">
			<h3 class="card-title align-items-start flex-column">
				<span class="card-label font-weight-bolder text-dark">{{ $t('VIEWS.MessageAdministration.Sms.title') }}</span>
			</h3>
			<div class="card-toolbar">
				<a href="" v-on:click.prevent="showSMS('new')" class="btn btn-icon btn-light btn-sm mx-1" :class="!hasPermission('action.survey.add') ? 'disabled' : ''">
					<i class="fas fa-plus text-primary"></i>
				</a>
			</div>
		</div>
		<div class="card-body py-3">
			<ContentLoading size="1.5" v-if="pageProcesses.isLoading"></ContentLoading>
			<NoData v-if="smsList.length === 0 && !pageProcesses.isLoading"></NoData>
			<div class="col-12 prinor-table" v-if="smsList.length > 0 && !pageProcesses.isLoading">
				<b-table ref="surveysTable" :data="smsList" :paginated="true" :per-page="15" default-sort="created_at" :default-sort-direction="'desc'" :sort-icon="'caret-up'">
					<b-table-column field="name" :label="$t('GENERAL.Forms.name')" v-slot="props" :sortable="true" :searchable="true" cell-class="text-valign">
						<template>
							<span class="truncate">{{ props.row.name }}</span>
						</template>
					</b-table-column>
					<b-table-column field="created_at" :label="$t('VIEWS.Survey.All.createdAt')" v-slot="props" :sortable="true" :searchable="false" cell-class="text-valign">
						<template>
							<span class="truncate">{{ new Date(props.row.created_at).toLocaleDateString() }}</span>
						</template>
					</b-table-column>
					<b-table-column field="updated_at" :label="$t('GENERAL.General.updatedAt')" v-slot="props" :sortable="true" :searchable="false" cell-class="text-valign">
						<template>
							<span class="truncate">{{ new Date(props.row.updated_at).toLocaleDateString() }}</span>
						</template>
					</b-table-column>
					<b-table-column field="id" :label="$t('GENERAL.General.actions')" v-slot="props" cell-class="text-right" header-class="text-right" width="15rem">
						<template>
							<a href="" v-on:click.prevent="showSMS(props.row.id)" class="btn btn-icon btn-light btn-sm m-1" :class="!hasPermission('action.survey.edit') ? 'disabled' : ''" :title="$t('MENU.Survey.editSurvey')">
								<i class="fas fa-edit text-primary"></i>
							</a>
							<a href="" @click.prevent="onDelete(props.row.id, props.row.name)" class="btn btn-icon btn-light btn-sm m-1" :class="!hasPermission('action.survey.delete') ? 'disabled' : ''" :title="$t('VIEWS.Survey.All.delete')">
								<i class="far fa-trash-alt text-danger"></i>
							</a>
						</template>
					</b-table-column>
				</b-table>
			</div>
		</div>
		<b-modal
			ref="edit-modal"
			content-class="modal-border"
			size="xl"
			:title="$t('VIEWS.MessageAdministration.Sms.edit') + ': ' + sms.name"
			:cancel-title="$t('GENERAL.Buttons.cancel')"
			:ok-title="$t('GENERAL.Forms.save')"
			v-on:ok="onUpdate"
			v-on:hidden="resetForm"
			scrollable
		>
			<div class="row">
				<div class="col-12">
					<b-form-group>
						<label>{{ $t('VIEWS.MessageAdministration.Mail.name') }}:</label>
						<b-input type="text" v-model="sms.name"></b-input>
					</b-form-group>
				</div>
				<div class="col-12 col-md-6">
					<b-form-group :label="$t('VIEWS.MessageAdministration.Mail.personalised')" v-slot="{ ariaDescribedby }">
						<b-form-radio-group v-model="sms.personalised" :aria-describedby="ariaDescribedby">
							<b-form-radio :value="false">{{ $t('GENERAL.General.no') }}</b-form-radio>
							<b-form-radio :value="true">{{ $t('GENERAL.General.yes') }}</b-form-radio>
						</b-form-radio-group>
					</b-form-group>
				</div>
				<div class="col-12 col-md-6">
					<b-form-group :label="$t('VIEWS.MessageAdministration.Mail.organization')" v-slot="{ ariaDescribedby }">
						<b-form-radio-group v-model="sms.organization" :aria-describedby="ariaDescribedby">
							<b-form-radio :value="false">{{ $t('GENERAL.General.no') }}</b-form-radio>
							<b-form-radio :value="true">{{ $t('GENERAL.General.yes') }}</b-form-radio>
						</b-form-radio-group>
					</b-form-group>
				</div>
				<div class="col-12">
					<b-form-group>
						<label>{{ $t('VIEWS.MessageAdministration.Mail.message') }}:</label>
						<b-textarea id="sms-message" v-model="sms.message" rows="6" ref="message"></b-textarea>
					</b-form-group>
					<b-popover target="sms-message" triggers="focus" placement="bottom" v-if="sms.personalised === true">
						<h6>{{ $t('VIEWS.Survey.Create.Placeholder.title') }}</h6>
						<template v-for="(name, value) in personalPlaceholder">
							<b-button v-if="value !== 'that'" variant="primary" class="m-2" v-on:click.prevent="personalPlaceholder.insertPlaceholder( name, 'sms', 'message')" :key="'sms-message-' + value">{{ $t('VIEWS.Survey.Create.Placeholder.' + value) }}</b-button>
						</template>
					</b-popover>
				</div>
			</div>
		</b-modal>
	</div>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { PRINOR_TOASTS } from '@/core/services/toast.service';
import ContentLoading from '@/view/component/misc/ContentLoading';
import Swal from 'sweetalert2';
import { SET_BREADCRUMB } from '@/core/services/store/breadcrumbs.module';
import { PRINOR_PROCESSES } from '@/core/services/actions.service';
import {SET_ACTION} from "@/core/services/store/actions.module";
import {mapGetters} from "vuex";
import {PRINOR_PERMISSION} from "@/core/services/permissions.service";
import Placeholder from "@/data/survey/Placeholder";
import NoData from "@/view/component/misc/NoData";

export default {
	name: 'Sms',
	components: {NoData, ContentLoading },
	data() {
		return {
			smsList: [],
			sms: {
				id: "",
				name: "",
				message: "",
				personalised: false,
				organization: false,
			}
		};
	},
	mounted() {
		this.$store.dispatch(SET_ACTION, []);
		this.$store.dispatch(SET_BREADCRUMB, [
			{
				title: this.$t('VIEWS.MessageAdministration.title'),
			},
			{ title: this.$t('VIEWS.MessageAdministration.Sms.title') },
		]);
		PRINOR_PROCESSES.isLoading();

		this.onGet();
	},
	computed: {
		...mapGetters(['pageProcesses']),

		personalPlaceholder: function() {
			return new Placeholder(this);
		},
	},
	methods: {
		hasPermission(name) {
			return PRINOR_PERMISSION.action(name);
		},
		showSMS: function(id) {
			if (id !== 'new') {
				let filtered = this.smsList.filter(value => {
					return value.id === id;
				});
				if (filtered.length === 1) {
					this.sms = filtered[0];

					if (this.sms.company_id == null) {
						this.sms.organization = true;
					}
					else {
						this.sms.organization = false;
					}

					this.$refs['edit-modal'].show();
				}
			} else {
				this.sms.id = 'new';
				this.$refs['edit-modal'].show();
			}
		},
		onGet: function() {
			this.isLoading = true;
			this.get().then(data => {
				this.smsList = data.data;
			});
		},
		get: function() {
			return new Promise(resolve => {
				ApiService.setHeader();
				ApiService.get('message-template/sms').then(data => {
					PRINOR_PROCESSES.reset();
					if (ApiService.checkSuccess(data)) {
						if (data.data.success === true) {
							resolve(data.data);
						} else {
							PRINOR_TOASTS.general.error(this, data.data.error);
						}
					}
				});
			});
		},
		resetForm: function() {
			this.sms = {
				id: "",
				name: "",
				message: "",
				personalised: false,
				organization: false,
			};
		},
		onUpdate() {
			PRINOR_PROCESSES.isUpdating();
			this.update().then(() => {
				this.resetForm();
				this.onGet();
			});
		},
		update() {
			return new Promise(resolve => {
				ApiService.setHeader();
				ApiService.post('message-template/sms', this.sms).then(data => {
					PRINOR_PROCESSES.reset();
					if (ApiService.checkSuccess(data)) {
						if (data.data.success === true) {
							PRINOR_TOASTS.save.confirmation(this);
						} else {
							PRINOR_TOASTS.save.error(this, data.data.error);
						}

						resolve(data.data);
					}
				});
			});
		},
		delete: function(id) {
			return new Promise(resolve => {
				ApiService.setHeader();
				ApiService.delete('message-template/sms/' + id).then(data => {
					PRINOR_PROCESSES.reset();
					if (ApiService.checkSuccess(data)) {
						if (data.data.success === true) {
							resolve(data.data);
						} else {
							PRINOR_TOASTS.general.error(this, data.data.error);
						}
					}
				});
			});
		},
		onDelete: function(id, name) {
			Swal.fire({
				title: this.$t('VIEWS.Survey.All.delete'),
				text: this.$t('VIEWS.Survey.All.deleteText', { name: name }),
				icon: 'question',
				confirmButtonText: this.$t('GENERAL.Forms.delete'),
				showCancelButton: true,
				cancelButtonText: this.$t('GENERAL.Buttons.cancel'),
				customClass: {
					confirmButton: 'btn btn-danger',
					cancelButton: 'btn btn-secondary',
				},
				allowOutsideClick: false,
				allowEscapeKey: false,
				allowEnterKey: false,
			}).then(result => {
				if (result.isConfirmed) {
					this.delete(id).then(data => {
						if (data.success === true) {
							this.onGet();
						} else {
							PRINOR_TOASTS.delete.error(this);
						}
					});
				}
			});
		},
	},
};
</script>

<style>
.text-valign {
	vertical-align: middle !important;
}
</style>
